
import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { useDocumentChecklist } from '../../hooks/useDocumentChecklist';
import { useDataRoom } from '../../context/DataRoomContext';
import DocumentProgressRing from './DocumentProgressRing';
import { DocumentRequirement } from '../../types/checklist';
import { ChevronLeft, ChevronRight, Eye, CheckCircle, X } from 'lucide-react';
import { cn } from '../../lib/utils';

interface DocumentChecklistSidebarProps {
  className?: string;
  isOpen: boolean;
  onClose: () => void;
}

const DocumentChecklistSidebar: React.FC<DocumentChecklistSidebarProps> = ({ 
  className, 
  isOpen, 
  onClose 
}) => {
  const { documents } = useDataRoom();
  const {
    requirements,
    progress,
    getRequirementStatus
  } = useDocumentChecklist();

  const requiredDocuments = requirements.filter(req => req.isRequired);

  // Enhanced circular progress component
  const EnhancedProgressRing = ({ percentage }: { percentage: number }) => (
    <div className="relative w-20 h-20">
      <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
        <circle
          cx="50"
          cy="50"
          r="45"
          stroke="#E5E7EB"
          strokeWidth="8"
          fill="none"
        />
        <circle
          cx="50"
          cy="50"
          r="45"
          stroke="#10B981"
          strokeWidth="8"
          fill="none"
          strokeDasharray={`${2 * Math.PI * 45}`}
          strokeDashoffset={`${2 * Math.PI * 45 * (1 - percentage / 100)}`}
          strokeLinecap="round"
          className="transition-all duration-1000 ease-in-out"
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="text-center">
          <div className="text-lg font-bold text-gray-900">{percentage}%</div>
          <div className="text-xs text-gray-500">Complete</div>
        </div>
      </div>
    </div>
  );

  if (!isOpen) return null;

  return (
    <div className={cn('fixed inset-y-0 right-0 z-50 w-96 bg-white shadow-xl border-l', className)}>
      <Card className="h-full rounded-none border-0 shadow-none">
        <CardHeader className="pb-3 border-b">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Document Checklist</CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="w-8 h-8 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          
          <div className="flex items-center justify-center pt-4">
            <EnhancedProgressRing percentage={progress.percentage} />
          </div>
          
          <div className="flex justify-between items-center pt-2">
            <div className="text-center">
              <div className="text-sm font-medium text-emerald-600">{progress.completed}</div>
              <div className="text-xs text-gray-500">Completed</div>
            </div>
            <div className="text-center">
              <div className="text-sm font-medium text-amber-600">{progress.inProgress}</div>
              <div className="text-xs text-gray-500">In Progress</div>
            </div>
            <div className="text-center">
              <div className="text-sm font-medium text-red-600">{progress.overdue}</div>
              <div className="text-xs text-gray-500">Overdue</div>
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="pt-4">
          <div className="space-y-3 max-h-[calc(100vh-300px)] overflow-y-auto">
            {requiredDocuments.map((requirement) => {
              const status = getRequirementStatus(requirement);
              const uploadedCount = requirement.uploadedDocuments.length;
              
              return (
                <div
                  key={requirement.id}
                  className={cn(
                    'p-3 rounded-lg border transition-all duration-200',
                    requirement.isCompleted 
                      ? 'bg-emerald-50 border-emerald-200' 
                      : 'bg-white border-gray-200 hover:border-gray-300'
                  )}
                >
                  <div className="flex items-start gap-3">
                    <DocumentProgressRing status={status} size="sm" />
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between">
                        <h4 className={cn(
                          'text-sm font-medium leading-tight',
                          requirement.isCompleted ? 'line-through text-gray-500' : 'text-gray-900'
                        )}>
                          {requirement.displayName}
                        </h4>
                        
                        {uploadedCount > 0 && (
                          <Badge variant="secondary" className="text-xs ml-2">
                            {uploadedCount} uploaded
                          </Badge>
                        )}
                      </div>
                      
                      {requirement.description && (
                        <p className="text-xs text-gray-500 mt-1">
                          {requirement.description}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          
          {progress.percentage === 100 && (
            <div className="mt-6 p-4 bg-emerald-50 rounded-lg border border-emerald-200">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-emerald-600" />
                <div>
                  <div className="text-sm font-medium text-emerald-900">
                    All Required Documents Complete!
                  </div>
                  <div className="text-xs text-emerald-600">
                    Your document submission is ready for review.
                  </div>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DocumentChecklistSidebar;
